import React from 'react';
import { X, Loader2, UserPlus, Shield, ImageIcon } from 'lucide-react';
import { at } from '../../../i18nAdmin';
import { Language, NewsItem } from '../../../types';

interface NewsAuditModalProps {
    isOpen: boolean;
    isAnimating: boolean;
    isLoading: boolean;
    selectedNews: NewsItem | null;
    newsReports: any[];
    onClose: () => void;
    onDeleteNews: (news: NewsItem) => void;
    lang: Language;
}

export const AdminNewsAuditModal: React.FC<NewsAuditModalProps> = ({
    isOpen, isAnimating, isLoading, selectedNews, newsReports, onClose, onDeleteNews, lang
}) => {
    if (!isOpen || !selectedNews) return null;

    return (
        <div className={`fixed inset-0 z-[400] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
            <div className={`relative bg-white w-full max-w-2xl rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden h-[85vh] md:h-[80vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                <header className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl overflow-hidden shadow-sm flex items-center justify-center bg-slate-100">
                            {selectedNews.image ? (
                                <img src={selectedNews.image} className="w-full h-full object-cover" alt="" />
                            ) : (
                                <ImageIcon size={20} className="text-slate-300" />
                            )}
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-none mb-1">{at(lang, 'intelligenceBreachAudit')}</h3>
                            <p className="text-[10px] font-bold text-rose-500 uppercase tracking-widest truncate max-w-[200px]">{selectedNews.title}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors shrink-0"><X size={24} /></button>
                </header>
                
                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    {isLoading ? (
                        <div className="h-full flex flex-col items-center justify-center gap-4">
                            <Loader2 className="animate-spin text-rose-500" size={32} />
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Retrieving Breach Data...</span>
                        </div>
                    ) : (
                        <div className="space-y-10 animate-in fade-in duration-500">
                            <section>
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">{at(lang, 'breachOrigin')}</h4>
                                <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <UserPlus size={18} className="text-slate-400" />
                                        <span className="text-sm font-black text-slate-700">{at(lang, 'source')}: {selectedNews.source}</span>
                                    </div>
                                    <span className="text-[10px] font-black text-rose-500 uppercase px-3 py-1 bg-rose-50 rounded-lg">{at(lang, 'reported')}</span>
                                </div>
                            </section>

                            <section>
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">{at(lang, 'breachTestimony')} ({newsReports.length})</h4>
                                <div className="space-y-3">
                                    {newsReports.map((r, i) => (
                                        <div key={i} className="p-6 bg-white border border-slate-100 rounded-[28px] shadow-sm">
                                            <div className="flex items-center gap-3 mb-3">
                                                <Shield size={14} className="text-rose-500" />
                                                <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Breach Report #{i+1}</span>
                                            </div>
                                            <p className="text-sm font-bold text-slate-600 leading-relaxed italic">"{r.reason || 'No specific reason provided'}"</p>
                                            <div className="mt-4 pt-4 border-t border-slate-50 flex justify-between items-center text-[9px] font-black text-slate-300 uppercase tracking-widest">
                                                <span>Scout ID: {r.reporter_id.substring(0, 8)}...</span>
                                                <span>{new Date(r.created_at).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {newsReports.length === 0 && (
                                        <div className="py-20 text-center opacity-30">
                                            <Shield size={48} className="mx-auto mb-4" />
                                            <p className="text-xs font-black uppercase tracking-widest">{at(lang, 'noTestimonyRecorded')}</p>
                                        </div>
                                    )}
                                </div>
                            </section>
                        </div>
                    )}
                </div>
                
                <footer className="p-8 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                    <button onClick={onClose} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest md:hover:text-slate-900 transition-colors">{at(lang, 'closeLog')}</button>
                    <button onClick={() => onDeleteNews(selectedNews)} className="flex-[2] py-4 bg-rose-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-rose-100 md:hover:bg-rose-600 transition-all active:scale-95">{at(lang, 'terminateIntelligence')}</button>
                </footer>
            </div>
        </div>
    );
};
